import { Button } from "@/components/primitives/button";
import { useNavigationState } from "@/zustand/NavigationState";
import { queryClient } from "@/queryClient";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";
import { useState } from "react";
import { removeMemberFromChat } from "@/middleware/members";

type Props = {
  open: boolean;
  onOpenClose: (open: boolean) => void;
  username: string;
};

export default function RemoveMemberFromChatDialog({
  open,
  onOpenClose,
  username,
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const activeChatId = useNavigationState((state) => state.activeChatId);

  const handleRemoveMemberFromChat = async () => {
    if (!activeChatId) {
      return;
    }

    try {
      setIsLoading(true);
      await removeMemberFromChat({
        chatId: activeChatId,
        username,
      });
      toast({
        duration: 1500,
        description: t("memberRemoved"),
        variant: "default",
      });

      // Invalidate the cached query results for the chat list of members
      queryClient.invalidateQueries({
        queryKey: ["chat-members", activeChatId],
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      onOpenClose(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (isLoading) {
          return;
        }
        onOpenClose(isOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("remove")}</DialogTitle>
          <DialogDescription>{t("areYouSure.toRemoveUser")}</DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            disabled={isLoading}
            onClick={() => onOpenClose(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="destructive"
            disabled={isLoading}
            onClick={() => handleRemoveMemberFromChat()}
          >
            {isLoading ? t("removing") : t("remove")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
